// Home-health pricing grid: PDGM or Per-visit and sequestration per payer, with every
// billed visit code as an editable rate. A per-visit payer's blank rate shows red.

import React, { useCallback, useEffect, useState } from "react";

const API_URL = process.env.REACT_APP_API_URL || "";
const PDGM = "EPISODE";
const PER_VISIT = "FFS";

const url = (practiceId, tail = "") =>
  `${API_URL}/api/practices/${practiceId}/fee-schedule/home-health-pricing${tail}`;

async function send(target, method, body) {
  const response = await fetch(target, {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) throw new Error((data && data.error) || "Could not save");
  return data;
}

function RateCell({ payer, code, disabled, missing, onSave }) {
  const saved = payer.rates[code] || "";
  const [value, setValue] = useState(saved);
  useEffect(() => setValue(saved), [saved]);
  return (
    <input
      aria-label={`${payer.payer_name} ${code}`}
      className={`fs-hh-rate${missing && !value ? " missing" : ""}`}
      inputMode="decimal"
      value={value}
      disabled={disabled}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => {
        if (value !== saved) onSave(payer, code, value);
      }}
    />
  );
}

export default function HomeHealthPricing({ practiceId, readOnly }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [from, setFrom] = useState("2026-01-01");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    send(url(practiceId), "GET")
      .then(setData)
      .catch((e) => setError(e.message));
  }, [practiceId]);

  useEffect(load, [load]);

  const setting = async (payer, body) => {
    setError(null);
    setSaving(true);
    try {
      await send(url(practiceId, `/${payer.id}`), "PUT", body);
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveRate = async (payer, code, rate) => {
    setError(null);
    try {
      await send(url(practiceId, `/${payer.id}/rates/${code}`), "PUT", {
        rate,
        effective_from: from,
      });
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  if (!data || !data.codes) {
    return error ? <div className="fs-message error">{error}</div> : null;
  }

  return (
    <div className="fs-card" data-testid="hh-pricing">
      <div className="fs-hh-head">
        <h2>Payer pricing</h2>
        <label>
          Rates from
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </label>
      </div>
      {error ? <div className="fs-message error">{error}</div> : null}
      <div className="fs-hh-scroll">
        <table className="fs-hh-table">
          <thead>
            <tr>
              <th className="fs-hh-payer">Payer</th>
              <th>Pricing</th>
              <th>Sequestration</th>
              <th className="n">% of Medicare</th>
              {data.codes.map((code) => (
                <th key={code} className="n">
                  {code}
                </th>
              ))}
              <th>Other rates</th>
            </tr>
          </thead>
          <tbody>
            {data.payers.map((p) => {
              const perVisit = p.pricing === PER_VISIT;
              const locked = readOnly || p.locked || saving;
              return (
                <tr key={p.id} data-testid="hh-pricing-row">
                  <td className="fs-hh-payer">{p.payer_name}</td>
                  <td>
                    <div className="fs-hh-toggle">
                      {[
                        [PDGM, "PDGM"],
                        [PER_VISIT, "Per-visit"],
                      ].map(([key, label]) => (
                        <button
                          key={key}
                          type="button"
                          aria-pressed={p.pricing === key}
                          className={p.pricing === key ? "on" : ""}
                          disabled={locked}
                          onClick={() => setting(p, { pricing: key })}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="fs-hh-seq">
                    <input
                      type="checkbox"
                      aria-label={`${p.payer_name} sequestration`}
                      checked={p.sequestration}
                      disabled={locked || p.pricing === "UNSET"}
                      onChange={(e) =>
                        setting(p, { sequestration: e.target.checked })
                      }
                    />
                  </td>
                  <td className="n">
                    <RateCell
                      payer={p}
                      code="PDGMP"
                      disabled={locked || p.pricing !== PDGM}
                      onSave={saveRate}
                    />
                  </td>
                  {data.codes.map((code) => (
                    <td key={code} className="n">
                      <RateCell
                        payer={p}
                        code={code}
                        disabled={readOnly || !perVisit}
                        missing={perVisit}
                        onSave={saveRate}
                      />
                    </td>
                  ))}
                  <td>{(p.other_rates || []).join(", ")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
